/**
 * Auth Forgot Controller (EPIC-001 / 1.4)
 *
 * Submits the email to POST /auth/forgot. The backend always answers
 * 202 (anti-enumeration D5) so we always render the success block on
 * resolution — only on a transient transport error (no response, 5xx) do
 * we surface a generic retry message.
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "../services/api.js";

export default class AuthForgotController extends BaseController {
  static targets = ["form", "email", "submit", "error", "successBlock"];

  connect() {
    this.clearError();
    if (this.hasEmailTarget) {
      queueMicrotask(() => this.emailTarget.focus());
    }
  }

  async submit(event) {
    event.preventDefault();

    const email = this.emailTarget.value.trim();
    this.clearError();

    if (!email || !this.isValidEmail(email)) {
      this.showFormError("Adresse email invalide.");
      this.emailTarget.focus();
      return;
    }

    this.setBusy(true);

    try {
      await api.post("/auth/forgot", { email });
      this.showSuccessBlock();
    } catch (err) {
      // 422 = malformed (bug client). 0/408/5xx = network — retry message.
      // Otherwise stay silent and show success: the API response is by
      // design indistinguishable, so an authentication-state attacker
      // cannot probe email existence.
      if (err instanceof APIError && err.status === 422) {
        this.showFormError("Adresse email invalide.");
      } else if (err instanceof APIError && (err.status === 0 || err.status === 408 || err.status >= 500)) {
        this.showFormError("Réseau injoignable. Réessayez.");
      } else {
        this.showSuccessBlock();
      }
    } finally {
      this.setBusy(false);
    }
  }

  // ---------------------------------------------------------------------------
  // View helpers
  // ---------------------------------------------------------------------------

  showSuccessBlock() {
    if (this.hasFormTarget) {
      this.formTarget.hidden = true;
    }
    if (this.hasSuccessBlockTarget) {
      this.successBlockTarget.hidden = false;
      this.successBlockTarget.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }

  showFormError(message) {
    if (!this.hasErrorTarget) return;

    const fragment = this.cloneTemplate("tpl-form-error");
    this.fillSlots(fragment, { message });

    this.errorTarget.replaceChildren(fragment);
    this.errorTarget.hidden = false;
  }

  clearError() {
    if (!this.hasErrorTarget) return;
    this.errorTarget.replaceChildren();
    this.errorTarget.hidden = true;
  }

  setBusy(busy) {
    if (!this.hasSubmitTarget) return;
    this.submitTarget.disabled = busy;
    this.submitTarget.setAttribute("aria-busy", busy ? "true" : "false");
  }
}
